import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Loader2, CheckCircle2, AlertTriangle, ClipboardList, RefreshCw, Users } from 'lucide-react';

const FLAG_META = {
  not_acknowledged: { label: 'Briefing not acknowledged', cls: 'bg-amber-100 text-amber-800' },
  incomplete_tasks: { label: 'Tasks incomplete', cls: 'bg-orange-100 text-orange-800' },
  missing_documentation: { label: 'Missing documentation', cls: 'bg-rose-100 text-rose-800' },
  missing_report: { label: 'No end-of-shift report', cls: 'bg-rose-100 text-rose-800' },
};

/**
 * Read-only live monitoring of talent task completion for a shift.
 * Used by clients, agencies and admins. Renders nothing when no checklist exists.
 */
export const ShiftTaskMonitor = ({ shiftId, pollMs = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tasks/shifts/${shiftId}/overview`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    load();
    if (pollMs > 0) {
      const t = setInterval(load, pollMs);
      return () => clearInterval(t);
    }
  }, [load, pollMs]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="task-monitor-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading task monitor…
      </div>
    );
  }
  if (!data || !data.items || data.items.length === 0) return null;

  const talent = data.talent || [];
  const totalTasks = data.items.length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="shift-task-monitor">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <ClipboardList className="w-4 h-4 text-cyan-600" /> Live Task Monitoring
        </span>
        <button onClick={load} className="text-xs text-cyan-700 flex items-center gap-1 hover:underline" data-testid="task-monitor-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {talent.length === 0 ? (
        <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="task-monitor-empty">
          <Users className="w-4 h-4" /> No talent booked on this shift yet.
        </p>
      ) : (
        <div className="space-y-2">
          {talent.map((t) => {
            const pct = totalTasks ? Math.round((t.done / totalTasks) * 100) : 0;
            return (
              <div key={t.booking_id} className="border border-slate-100 rounded-lg p-3" data-testid={`monitor-talent-${t.booking_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-800" data-testid={`monitor-name-${t.booking_id}`}>{t.talent_name}</span>
                  <span className="text-xs font-medium text-slate-600" data-testid={`monitor-progress-${t.booking_id}`}>{t.done}/{t.total} tasks</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                  <div className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 ${t.acknowledged ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    <CheckCircle2 className="w-3 h-3" /> {t.acknowledged ? 'Briefing OK' : 'Not briefed'}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.upload_count} file{t.upload_count === 1 ? '' : 's'}</span>
                  {data.report_required && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${t.report_submitted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {t.report_submitted ? 'Report in' : 'No report'}
                    </span>
                  )}
                  {(t.flags || []).map((f) => (
                    <span key={f} className={`text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 ${(FLAG_META[f] || {}).cls || 'bg-rose-100 text-rose-800'}`}
                          data-testid={`monitor-flag-${t.booking_id}-${f}`}>
                      <AlertTriangle className="w-3 h-3" /> {(FLAG_META[f] || {}).label || f}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShiftTaskMonitor;
